import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { Row, Col } from "react-flexbox-grid";
import { Button } from "react-md";
import { FontIcon } from "react-md";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextField as TextField2 } from "@material-ui/core";
import TestIncludesList from "./TestIncludesList";
import debounce from "debounce";
import Fuse from "fuse.js";
import doubleArrowRight from "../../../../../../assets/icons/keyboard_double_arrow_right.svg";
import deleteIcon from "../../../../../../assets/icons/delete_outline.svg";

// Every serviceTags entry the backend sent for this service, as chips under the test name.
// The list is free-form ops copy — "Fasting required", "For males only", "For above 32 only"
// — so nothing here filters or reorders it. (This row once hardcoded the fasting chip alone
// and disagreed with the test-details dialog, which has since become the inline
// TestIncludesList.)
//
// `tags` is populated by CartDashboardUtils._mapChildServices. The fastingRequired fallback
// covers a cart item that reached us without it, so the row can never lose a chip it used
// to render.
const renderTestTags = (test) => {
  const tags =
    Array.isArray(test.tags) && test.tags.length > 0
      ? test.tags
      : test.fastingRequired
        ? ["Fasting required"]
        : [];
  if (tags.length === 0) return null;

  return (
    <div className="cdv-cart-test-tags">
      {tags.map((tag, index) => (
        <span key={index} className="cdv-cart-test-tag">
          {tag}
        </span>
      ))}
    </div>
  );
};

const DocCartView = (props) => {
  const {
    disabled = false,
    readOnly = false,
    // Shown beside the "Cart details" heading in read-only mode, where the cart is an existing
    // record an operator may need to quote. The editable flows omit it: a CREATE cart has no id
    // until it is saved, so the heading would flicker one into place mid-flow.
    cartId = null,
    // The host page owns its own heading rhythm, and this component is embedded in two of them:
    // the cart screen, whose sections are H3s, and the navigo appointment page, where every
    // section is an H4 + margin class. Hardcoding H3 made "Cart details" out-scale every heading
    // around it there. Defaults preserve the cart screen exactly as-is.
    headingTag: HeadingTag = "h3",
    headingClassName = "",
    cartItems = [],
    cartConfirmed = false,
    cartLoading = false,
    documentsList = [],
    selectedDocument = null,
    providers = [],
    selectedProvider = null,
    addressSelected = null,
    onUploadDocument,
    onSelectDocument,
    // Detaches a document from the cart. Only wired into the editable tree below, which
    // renders solely when `readOnly` is false — i.e. the cart is a DRAFT or PENDING_REVIEW.
    onDeleteDocument,
    onAddToCart,
    onRemoveFromCart,
    onConfirmCart,
    onModifyCart,
    onBookAppointment,
    onSelectProvider,
    cartDashboardUtils,
    docToken = "",
    planId = null,
    patientId = null,
    groupServiceId = null,
    // The "Wallet not eligible" tag only carries meaning on a plan that has a wallet:
    // when the plan has none, the wallet is never considered in the sublimit/payment
    // calls, so every test would read as not-eligible. Defaults to false so the tags
    // stay hidden until the plan's wallet status is actually known.
    planWalletIncluded = false,
    // The booking is for a family member the plan does not let the Zyla wallet be spent on.
    // When true the per-item "Wallet not eligible" tags are suppressed: a relation block makes
    // EVERY item ineligible, so per-row tags stop distinguishing anything and bury the tests
    // that are genuinely wallet-ineligible in their own right. The explanation itself is shown
    // once, next to the "Booking for" dropdown in UserDetailsSection — not here.
    walletBlockedForMember = false,
    // A booked cart's frozen price break-up (see CartDashboardUtils.applyServiceBreakup).
    // `priceRows` are the priced services that are not cart items — the home-sample collection
    // charge — listed under the tests so the prices visibly add up. `priceTotal` is the booked
    // total, which supersedes the provider's live total when present.
    priceRows = [],
    priceTotal = null,
  } = props;

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [defaultResults, setDefaultResults] = useState([]);
  const [defaultLoading, setDefaultLoading] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  // Cart items whose "Contains n tests" list is expanded inline (several may be open).
  const [expandedTestIds, setExpandedTestIds] = useState(() => new Set());
  // PDF docs are fetched into a same-origin blob: URL (see effect below); images
  // render straight from their URL, so these only ever hold the current PDF.
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  // Zoom factor for image documents (1 = fit-to-width). PDFs use the browser
  // viewer's own zoom, so this only applies to <img> docs.
  const [imageZoom, setImageZoom] = useState(1);
  // True while the image overflows its viewport, i.e. there is something to pan to. Drives the
  // grab cursor so it only appears when dragging would actually move something.
  const [canPan, setCanPan] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const fileInputRef = useRef(null);
  const imgScrollRef = useRef(null);
  // Where the pointer went down, plus the scroll offsets at that moment. Held in a ref so the
  // move handler reads it without re-subscribing on every pixel of movement.
  const panOriginRef = useRef(null);
  // Scroll offsets staged by a zoom step, applied after the browser has laid the image out at
  // its new width (see the layout effect below).
  const pendingScrollRef = useRef(null);

  // Does the image currently overflow its container in either axis?
  const measurePan = useCallback(() => {
    const el = imgScrollRef.current;
    if (!el) return;
    setCanPan(
      el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1,
    );
  }, []);

  // Zoom about the CENTRE of what is on screen rather than the image's top-left corner. Without
  // this the viewport keeps its scroll offsets while the image grows underneath them, so whatever
  // the reviewer was looking at slides away and every zoom step has to be chased with a scroll.
  //
  // The new offsets can only be applied once the image has been laid out at the new width, so they
  // are staged here and written in the layout effect below.
  const zoomTo = useCallback(
    (nextZoom) => {
      const clamped = Math.min(4, Math.max(0.25, +nextZoom.toFixed(2)));
      const el = imgScrollRef.current;
      if (el && imageZoom > 0 && clamped !== imageZoom) {
        const ratio = clamped / imageZoom;
        const centreX = el.scrollLeft + el.clientWidth / 2;
        const centreY = el.scrollTop + el.clientHeight / 2;
        pendingScrollRef.current = {
          left: centreX * ratio - el.clientWidth / 2,
          top: centreY * ratio - el.clientHeight / 2,
        };
      }
      setImageZoom(clamped);
    },
    [imageZoom],
  );

  // Back to fit-to-width, scrolled home — a reset that left the viewport deep in the old image
  // would look like nothing had happened.
  const resetZoom = useCallback(() => {
    pendingScrollRef.current = { left: 0, top: 0 };
    setImageZoom(1);
  }, []);

  // Runs before paint, so the re-centred scroll offsets land in the same frame as the new image
  // width and the zoom reads as a single movement instead of a jump-then-correct.
  useLayoutEffect(() => {
    const el = imgScrollRef.current;
    if (!el) return;
    const pending = pendingScrollRef.current;
    if (pending) {
      el.scrollLeft = pending.left;
      el.scrollTop = pending.top;
      pendingScrollRef.current = null;
    }
    measurePan();
  }, [imageZoom, selectedDocument?.id, measurePan]);

  // Drag-to-pan. The listeners live on window, not the scroll container, so a drag that runs off
  // the edge of the panel — which is exactly what happens when panning to an image's far side —
  // keeps tracking instead of dying the moment the pointer leaves.
  useEffect(() => {
    if (!isPanning) return undefined;

    const handleMove = (e) => {
      const el = imgScrollRef.current;
      const origin = panOriginRef.current;
      if (!el || !origin) return;
      // Content follows the pointer: dragging left reveals what is off to the right.
      el.scrollLeft = origin.left - (e.clientX - origin.x);
      el.scrollTop = origin.top - (e.clientY - origin.y);
    };

    const handleUp = () => {
      panOriginRef.current = null;
      setIsPanning(false);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isPanning]);

  const handlePanStart = (e) => {
    const el = imgScrollRef.current;
    if (!el || e.button !== 0) return;
    const hasOverflow =
      el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
    if (!hasOverflow) return;
    panOriginRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
    };
    setIsPanning(true);
    // Suppresses the browser's native image drag (the ghost-image + "no drop" cursor), which
    // otherwise swallows the gesture and is why the image could not be dragged at all.
    e.preventDefault();
  };

  const getDocUrl = () => {
    if (!selectedDocument?.url) return "";
    const baseUrl = selectedDocument.url.split("?")[0];
    return `${baseUrl}?token=${encodeURIComponent(docToken)}`;
  };

  const isImageDoc = () => {
    if (!selectedDocument?.url) return false;
    const path = selectedDocument.url.split("?")[0].toLowerCase();
    return /\.(jpe?g|png|gif|webp|bmp|svg)$/.test(path);
  };

  // A remote PDF URL can't be shown directly in an <iframe> (cross-origin auth +
  // X-Frame-Options / download disposition), which is why PDFs weren't loading.
  // Fetch the PDF as a blob using the same token query the <img> tags rely on,
  // then render it from a same-origin blob: object URL. Images are unaffected —
  // they keep rendering straight from the URL. Re-runs on every document/token
  // change so a mixed image+PDF list always swaps to the correct viewer, and the
  // previous blob URL is revoked on cleanup to avoid leaks.
  useEffect(() => {
    const url = getDocUrl();
    setPdfError(false);
    setImageZoom(1); // reset zoom whenever the selected document changes
    if (!selectedDocument || !url || isImageDoc()) {
      setPdfBlobUrl(null);
      setPdfLoading(false);
      return;
    }
    let cancelled = false;
    let objectUrl = null;
    setPdfBlobUrl(null);
    setPdfLoading(true);
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(objectUrl);
        setPdfLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPdfError(true);
        setPdfLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedDocument?.id, selectedDocument?.url, docToken]);

  // Shared document preview used by both the read-only and editable panels.
  // Images render straight from the URL; PDFs render from the fetched blob.
  const renderDocViewer = () => {
    const docUrl = getDocUrl();
    if (!selectedDocument) return null;
    if (!docUrl) {
      return (
        <div className="cdv-doc-prescription">
          <p style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            Document preview not available — no URL found
          </p>
        </div>
      );
    }
    if (isImageDoc()) {
      // Image is shown at its real aspect ratio (height auto), zoomed by width.
      // Toolbar gives zoom in/out, reset and an open-in-new-tab escape hatch.
      return (
        <div className="cdv-doc-prescription">
          <div className="cdv-doc-img-toolbar">
            <button
              type="button"
              className="cdv-doc-img-btn"
              title="Zoom out"
              onClick={() => zoomTo(imageZoom - 0.25)}
            >
              −
            </button>
            <span className="cdv-doc-img-zoom-level">
              {Math.round(imageZoom * 100)}%
            </span>
            <button
              type="button"
              className="cdv-doc-img-btn"
              title="Zoom in"
              onClick={() => zoomTo(imageZoom + 0.25)}
            >
              +
            </button>
            <button
              type="button"
              className="cdv-doc-img-btn cdv-doc-img-btn-text"
              title="Reset zoom"
              onClick={resetZoom}
            >
              Reset
            </button>
            <a
              href={docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cdv-doc-img-newtab"
            >
              Open in new tab ↗
            </a>
          </div>
          <div
            ref={imgScrollRef}
            className={`cdv-doc-img-scroll${
              canPan ? " cdv-doc-img-scroll--pannable" : ""
            }${isPanning ? " cdv-doc-img-scroll--panning" : ""}`}
            onMouseDown={handlePanStart}
          >
            <img
              key={`${selectedDocument.id}`}
              src={docUrl}
              alt="Document Viewer"
              className="cdv-doc-image"
              style={{ width: `${imageZoom * 100}%` }}
              // The natural size is only known once the image has loaded, and a tall image
              // overflows vertically even at 100% — so the pan affordance is measured then too.
              onLoad={measurePan}
              // Belt-and-braces with preventDefault in handlePanStart: neither the native drag
              // nor a text-selection drag may hijack the pan gesture.
              draggable={false}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="cdv-doc-prescription">
        {pdfBlobUrl ? (
          <iframe
            key={selectedDocument.id}
            src={pdfBlobUrl}
            title="Document Viewer"
            className="cdv-doc-pdf"
          />
        ) : (
          <p style={{ textAlign: "center", padding: "40px", color: "#999" }}>
            {pdfError ? "Document preview not available" : "Loading document…"}
          </p>
        )}
      </div>
    );
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (!query || query.trim().length < 1) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      if (!groupServiceId) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      cartDashboardUtils.searchTests(
        query,
        { planId, patientId, groupServiceId },
        (results) => {
          // Filter out items already in cart
          const filtered = results.filter(
            (r) => !cartItems.find((c) => c.id === r.id),
          );
          setSearchResults(filtered);
          setSearchLoading(false);
        },
        (err) => {
          setSearchResults([]);
          setSearchLoading(false);
        },
      );
    }, 400),
    [cartItems, planId, patientId, groupServiceId],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleSelectSearchResult = (test) => {
    onAddToCart(test);
    setSearchText("");
    setSearchResults([]);
  };

  // Prepopulate the search dropdown with the full available-tests list so the
  // reviewer can just focus the box and click to add — mirroring the patient
  // app's "Prescribed diagnostics" list instead of forcing them to type a term
  // before anything shows. Uses the same endpoint as search (empty text).
  // Re-runs when the plan/group/patient context or editability changes.
  useEffect(() => {
    if (cartConfirmed || disabled || !groupServiceId) {
      setDefaultResults([]);
      setDefaultLoading(false);
      return;
    }
    setDefaultLoading(true);
    cartDashboardUtils.fetchAvailableTests(
      { planId, patientId, groupServiceId },
      (results) => {
        setDefaultResults(results);
        setDefaultLoading(false);
      },
      () => {
        setDefaultResults([]);
        setDefaultLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartConfirmed, disabled, groupServiceId, planId, patientId]);

  // The server only matches the service code (`name`, e.g. "CTEST1") against
  // includingText, so a reviewer who types a sub-test ("T3 Total") or a LOINC
  // code ("3053-6") — which is how tests are actually referred to on a
  // prescription — gets nothing back. We already prefetch the whole available
  // catalogue for the prepopulated dropdown, so index it locally and fuzzy
  // search across the display name, the included sub-test names and their LOINC
  // codes. Weights rank a display-name hit above a sub-test hit; they must sum
  // to <= 1 (Fuse throws otherwise).
  const fuse = useMemo(
    () =>
      new Fuse(defaultResults, {
        keys: [
          { name: "serviceName", weight: 0.4 },
          { name: "name", weight: 0.15 },
          { name: "includes.name", weight: 0.3 },
          { name: "includes.loincCode", weight: 0.15 },
        ],
        // 0.3 keeps typo tolerance ("blod count" -> Complete Blood Count)
        // without dragging in unrelated services on short queries.
        threshold: 0.3,
        distance: 200,
        minMatchCharLength: 2,
        includeMatches: true,
        shouldSort: true,
      }),
    [defaultResults],
  );

  // Local fuzzy hits, each tagged with the sub-test that caused it to surface
  // (only when the service's own name didn't match) so the row can show *why*
  // it's in the list — otherwise "3053-6" returning "ChildTest1" looks random.
  const fuzzyResults = useMemo(() => {
    const query = searchText.trim();
    if (!query || defaultResults.length === 0) return [];
    return fuse.search(query).map(({ item, matches }) => {
      const hits = matches || [];
      const nameMatched = hits.some(
        (m) => m.key === "serviceName" || m.key === "name",
      );
      const testHit = hits.find(
        (m) => m.key === "includes.name" || m.key === "includes.loincCode",
      );
      const matchedTest =
        !nameMatched && testHit
          ? (item.includes || [])[testHit.arrayIndex]
          : null;
      return { ...item, matchedTest: matchedTest || null };
    });
  }, [fuse, searchText, defaultResults]);

  // Fuzzy hits first (ranked), then any server hit the local index missed —
  // which is the safety net for when the prefetch failed or the catalogue grew
  // past what we paged in. Cart items are excluded from both.
  const mergedSearchResults = useMemo(() => {
    const seen = new Set(fuzzyResults.map((r) => r.id));
    const serverOnly = searchResults.filter((r) => !seen.has(r.id));
    return [...fuzzyResults, ...serverOnly].filter(
      (r) => !cartItems.find((c) => c.id === r.id),
    );
  }, [fuzzyResults, searchResults, cartItems]);

  const toggleTestIncludes = (testId) => {
    setExpandedTestIds((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUploadDocument(file);
    }
    // Reset input so re-uploading same file triggers onChange
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Build dropdown options for document selection
  const docDropdownOptions = documentsList.map((doc) => ({
    label: doc.id,
    value: doc.id,
    doc: doc,
  }));

  // A booked cart's frozen total wins over the provider's live total: it is what was actually paid.
  const totalPrice = priceTotal ?? selectedProvider?.totalPrice;
  const allTestsAvailable =
    cartItems.length > 0 && cartItems.every((item) => item.available);
  // The selected provider offers the test but the patient does not qualify for it (age/gender
  // rules — `isNotEligible` on the coverage response). Blocks booking exactly like an
  // unavailable test does, so the two are gated together.
  const hasNotEligibleTests = cartItems.some((item) => item.notEligible);
  // Either flag makes a test a blocker: it has to be removed, or the provider changed.
  const isBlockedTest = (test) => !test.available || test.notEligible === true;

  const formatPrice = (price) => `₹${Number(price).toLocaleString("en-IN")}`;

  // Formats the patient's selected address the same way the address dropdown does.
  const formatPatientAddress = (addr) => {
    if (!addr) return "";
    return [
      addr.line1,
      addr.line2,
      addr.landmark,
      addr.city,
      addr.district,
      addr.state,
      addr.pincode,
    ]
      .filter(Boolean)
      .join(", ");
  };

  // Google Maps link for a patient address built from its lat/lng coordinates.
  const getCoordinatesMapLink = (addr) => {
    const coords = addr?.coordinates;
    if (
      coords &&
      coords.latitude != null &&
      coords.longitude != null &&
      coords.latitude !== 0 &&
      coords.longitude !== 0
    ) {
      return `https://www.google.com/maps/search/?api=1&query=${coords.latitude},${coords.longitude}`;
    }
    return "";
  };

  // Centre-visit providers show the provider's centre address (+ its Google Maps
  // link); home-sample providers show the patient's selected booking address
  // (+ a link built from that address's coordinates).
  const isHomeSample = selectedProvider?.isHomeSample;
  const addressLabel = isHomeSample ? "Booking address" : "Centre address";
  const addressValue = isHomeSample
    ? formatPatientAddress(addressSelected)
    : selectedProvider?.address;
  const addressMapLink = isHomeSample
    ? getCoordinatesMapLink(addressSelected)
    : selectedProvider?.googleMapLink;

  // Shared address row used by both the read-only and editable cart panels.
  // The double-arrow is always shown; it links out to Google Maps when a
  // destination (provider link or address coordinates) is available.
  const renderAddressBlock = () => (
    <div className="cdv-cart-addr">
      <div className="cdv-cart-field-label">{addressLabel}</div>
      <div className="cdv-cart-addr-value">
        <span>{addressValue || "-"}</span>
        {addressMapLink ? (
          <a
            href={addressMapLink}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in Google Maps"
          >
            <img
              src={doubleArrowRight}
              alt="Open in Google Maps"
              className="cdv-cart-addr-icon"
            />
          </a>
        ) : (
          <img src={doubleArrowRight} alt="" className="cdv-cart-addr-icon" />
        )}
      </div>
    </div>
  );

  if (readOnly) {
    return (
      <div>
        <HeadingTag className={headingClassName || undefined}>
          {`Cart details${cartId != null && cartId !== "" ? ` (${cartId})` : ""}`}
        </HeadingTag>
        <div className="cdv-container">
          <Row className="cdv-row">
            {/* Left Panel - Document Viewer. Always rendered in read-only so the
                Doc/Cart layout stays 50/50; when the cart has no prescription
                documents the panel shows an empty state instead of collapsing. */}
            <Col xs={12} sm={12} md={6} lg={6} className="cdv-col-left">
              <div className="cdv-doc-panel">
                <div className="cdv-doc-header">
                  <div>
                    {documentsList.length > 1 ? (
                      <div className="cdv-doc-id-dropdown">
                        <select
                          className="cdv-doc-select"
                          value={selectedDocument?.id || ""}
                          onChange={(e) => {
                            const doc = documentsList.find(
                              (d) => String(d.id) === e.target.value,
                            );
                            if (doc) onSelectDocument(doc);
                          }}
                        >
                          {documentsList.map((doc) => (
                            <option key={doc.id} value={doc.id}>
                              Doc ID: {doc.id}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="cdv-doc-id">
                        <span>
                          {documentsList.length > 0
                            ? `Doc ID: ${selectedDocument?.id}`
                            : "Documents"}
                        </span>
                      </div>
                    )}
                    {selectedDocument?.createdAt && (
                      <div className="cdv-doc-date">
                        {new Date(selectedDocument.createdAt).toLocaleString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="cdv-doc-content">
                  {documentsList.length > 0 ? (
                    renderDocViewer()
                  ) : (
                    <div className="cdv-doc-prescription">
                      <p
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#999",
                        }}
                      >
                        No documents uploaded
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Col>

            {/* Right Panel - Cart. The inner wrapper is the positioning context for
                the test-details panel, which overlays this column only so the
                prescription on the left stays readable next to it.
                cdv-col-right is what gives the column its definite height (and the
                flex context under it); without it .cdv-cart-panel is content-sized,
                so .cdv-cart-body's overflow-y never has a bounded height to scroll
                against and a cart of more than ~4 items is simply clipped by the
                container instead of scrolling — same class the editable tree uses. */}
            <Col xs={12} sm={12} md={6} lg={6} className="cdv-col-right">
              <div className="cdv-cart-col-inner">
                <div className="cdv-cart-panel">
                <div className="cdv-cart-header">
                  <div className="cdv-cart-title">Cart</div>
                  {cartItems.length > 0 && totalPrice > 0 && (
                    <div className="cdv-cart-total">
                      Total: {formatPrice(totalPrice)}
                    </div>
                  )}
                </div>
                <div className="cdv-cart-body">
                  {/* Service Provider (read-only outlined box) */}
                  <div className="cdv-cart-outlined">
                    <span className="cdv-cart-outlined-label">
                      Service provider
                    </span>
                    <span className="cdv-cart-outlined-value">
                      {selectedProvider
                        ? selectedProvider.id != null
                          ? `(${selectedProvider.id}) ${selectedProvider.name}`
                          : selectedProvider.name
                        : "—"}
                    </span>
                  </div>

                  {/* Address (opens Google Maps when available) */}
                  {renderAddressBlock()}

                  {/* Distance from booking address (centre providers) */}
                  {!selectedProvider?.isHomeSample &&
                    selectedProvider?.distanceInKm != null && (
                      <div className="cdv-cart-outlined">
                        <span className="cdv-cart-outlined-label">
                          Distance from booking address
                        </span>
                        <span className="cdv-cart-outlined-value">
                          {selectedProvider.distanceInKm} km away
                        </span>
                      </div>
                    )}

                  {/* Info Banner */}
                  {selectedProvider && (
                    <div className="cdv-cart-banner">
                      {selectedProvider?.bannerText ||
                        (selectedProvider?.isHomeSample
                          ? "Lab tests and scans will be scheduled at your booking address"
                          : "Lab tests and scans will be conducted at selected centre address")}
                    </div>
                  )}

                  {/* Test List (read-only — no delete) */}
                  {cartItems.length > 0 && (
                    <div className="cdv-cart-test-list">
                      {cartItems.map((test) => (
                        <div key={test.id} className="cdv-cart-test-item">
                          <div className="cdv-cart-test-row">
                            <div className="cdv-cart-test-info">
                              <div className="cdv-cart-test-name">
                                <span className="cdv-cart-test-name-text">
                                  {test.serviceName || test.name}
                                </span>
                                {planWalletIncluded &&
                                  !walletBlockedForMember &&
                                  test.isEligibleForWallet !== true && (
                                    <span className="cdv-cart-wallet-tag">
                                      Wallet not eligible
                                    </span>
                                  )}
                              </div>
                              <div
                                className="cdv-cart-test-toggle"
                                role="button"
                                aria-expanded={expandedTestIds.has(test.id)}
                                onClick={() => toggleTestIncludes(test.id)}
                              >
                                {`Contains ${test.subTestCount} tests`}
                                <span
                                  className={`cdv-cart-test-chevron${
                                    expandedTestIds.has(test.id)
                                      ? " cdv-cart-test-chevron--open"
                                      : ""
                                  }`}
                                >
                                  <FontIcon
                                    className="notif-preference-icon"
                                    style={{ color: "#f97f0f" }}
                                  >
                                    chevron_right
                                  </FontIcon>
                                </span>
                              </div>
                              {expandedTestIds.has(test.id) && (
                                <TestIncludesList includes={test.includes} />
                              )}
                              {test.price != null && (
                                <div className="cdv-cart-test-price">
                                  {formatPrice(test.price)}
                                </div>
                              )}
                            </div>
                          </div>
                          {renderTestTags(test)}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Priced services that are not cart items — the home-sample collection
                      charge — so the item prices above visibly add up to the total. */}
                  {priceRows.length > 0 && (
                    <div className="cdv-cart-extra-price-list">
                      {priceRows.map((row) => (
                        <div
                          key={row.serviceId}
                          className="cdv-cart-extra-price-row"
                        >
                          <span className="cdv-cart-extra-price-name">
                            {row.serviceName || `Service #${row.serviceId}`}
                          </span>
                          <span className="cdv-cart-extra-price-value">
                            {formatPrice(row.price)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div>
        <HeadingTag className={headingClassName || undefined}>
          {`Cart details`}
        </HeadingTag>
        {/* Until the last user-details field (Select address) is filled the
            screen passes disabled=true: keep the real Doc/Cart layout visible
            but greyed out and non-interactive (pointer-events: none). */}
        <div
          className="cdv-container"
          style={disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
        >
          <Row className="cdv-row">
            {/* Left Panel - Document Viewer */}
            <Col xs={12} sm={12} md={6} lg={6} className="cdv-col-left">
              <div className="cdv-doc-panel">
                {/* Doc Header */}
                <div className="cdv-doc-header">
                  <div>
                    {documentsList.length > 0 ? (
                      <div className="cdv-doc-id-dropdown">
                        <select
                          className="cdv-doc-select"
                          value={selectedDocument?.id || ""}
                          onChange={(e) => {
                            const doc = documentsList.find(
                              (d) => String(d.id) === e.target.value,
                            );
                            if (doc) onSelectDocument(doc);
                          }}
                        >
                          {documentsList.map((doc) => (
                            <option key={doc.id} value={doc.id}>
                              Doc ID: {doc.id}
                            </option>
                          ))}
                        </select>
                        {/* Upload as last option in dropdown area */}
                        <span
                          className="cdv-doc-upload-inline"
                          onClick={triggerFileInput}
                          title="Upload another document"
                        >
                          <i
                            className="material-icons"
                            style={{
                              fontSize: "18px",
                              color: "#f97f0f",
                              cursor: "pointer",
                            }}
                          >
                            add_circle_outline
                          </i>
                        </span>
                        {/* Detach the selected document from the cart. Blocked while a cart
                            operation is in flight so two rapid deletes can't each compute the
                            remaining ids from a stale list. */}
                        <span
                          className="cdv-doc-delete-inline"
                          onClick={() => {
                            if (!cartLoading && selectedDocument) {
                              onDeleteDocument(selectedDocument);
                            }
                          }}
                          title="Remove this document from the cart"
                          style={
                            cartLoading
                              ? { pointerEvents: "none", opacity: 0.4 }
                              : undefined
                          }
                        >
                          <img
                            src={deleteIcon}
                            alt="Remove document"
                            className="cdv-doc-delete-icon"
                          />
                        </span>
                      </div>
                    ) : (
                      <div className="cdv-doc-id">
                        <span>Doc ID:</span>
                      </div>
                    )}
                    {selectedDocument && (
                      <div className="cdv-doc-date">
                        {new Date(selectedDocument.createdAt).toLocaleString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </div>
                    )}
                  </div>
                  {!selectedDocument && (
                    <div>
                      <Button
                        raised
                        className="cdv-upload-btn"
                        onClick={triggerFileInput}
                        disabled={cartLoading}
                      >
                        {cartLoading ? "Uploading..." : "Upload document"}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Hidden file input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                />

                {/* Doc Content Area */}
                <div className="cdv-doc-content">{renderDocViewer()}</div>
              </div>
            </Col>

            {/* Right Panel - Cart. Same wrapper as the read-only tree: it anchors the
                test-details panel to this column so the document viewer stays visible. */}
            <Col xs={12} sm={12} md={6} lg={6} className="cdv-col-right">
              <div className="cdv-cart-col-inner">
                <div className="cdv-cart-panel">
                {/* Cart Header */}
                <div className="cdv-cart-header">
                  <div className="cdv-cart-title">Cart</div>
                  {cartConfirmed && cartItems.length > 0 && totalPrice > 0 && (
                    <div className="cdv-cart-total">
                      Total: {formatPrice(totalPrice)}
                    </div>
                  )}
                </div>

                {/* Cart Body */}
                <div className="cdv-cart-body">
                  {cartConfirmed ? (
                    <>
                      {/* Service Provider (outlined select) */}
                      <div className="cdv-cart-outlined cdv-cart-outlined--select">
                        <span className="cdv-cart-outlined-label">
                          Service provider
                        </span>
                        <select
                          className="cdv-provider-select"
                          value={selectedProvider?.id ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            const provider = providers.find(
                              (p) => String(p.id) === val,
                            );
                            if (provider) onSelectProvider(provider);
                          }}
                        >
                          {/* Placeholder shown only when no provider is selected (e.g. after
                              a deletion left the previously-selected provider uncovered) so the
                              dropdown reads as unselected and forces a conscious re-pick. */}
                          {!selectedProvider && (
                            <option value="" disabled>
                              Select service provider
                            </option>
                          )}
                          {providers.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                        <i className="material-icons cdv-cart-outlined-caret">
                          expand_more
                        </i>
                      </div>

                      {/* Address (opens Google Maps when available) */}
                      {renderAddressBlock()}

                      {/* Distance from booking address (centre providers) */}
                      {!selectedProvider?.isHomeSample &&
                        selectedProvider?.distanceInKm != null && (
                          <div className="cdv-cart-outlined">
                            <span className="cdv-cart-outlined-label">
                              Distance from booking address
                            </span>
                            <span className="cdv-cart-outlined-value">
                              {selectedProvider.distanceInKm} km away
                            </span>
                          </div>
                        )}

                      {/* Info Banner */}
                      <div className="cdv-cart-banner">
                        {selectedProvider?.bannerText ||
                          "Lab tests and scans will be conducted at selected centre address"}
                      </div>
                    </>
                  ) : (
                    /* Search Bar */
                    <div className="cdv-cart-search-wrapper">
                      <div className="cdv-cart-search">
                        <span className="cdv-cart-search-icon">
                          <i className="material-icons">search</i>
                        </span>
                        <input
                          type="text"
                          className="cdv-cart-search-input"
                          placeholder="Search tests or scans"
                          value={searchText}
                          onChange={handleSearchChange}
                          onFocus={() => setSearchFocused(true)}
                          onBlur={() => setSearchFocused(false)}
                        />
                      </div>
                      {/* Search Results Dropdown. Opens on focus and — when
                          nothing is typed — is prepopulated with every available
                          test/scan (fetched up front) so the reviewer can click
                          to add without guessing a term. Typing replaces the
                          list with server-side search results. */}
                      {searchFocused &&
                        (() => {
                          const searching = searchText.trim().length >= 1;
                          const results = searching
                            ? mergedSearchResults
                            : defaultResults.filter(
                                (r) => !cartItems.find((c) => c.id === r.id),
                              );
                          // Local fuzzy hits are instant, so only fall back to
                          // the "Searching..." row while the debounced server
                          // call is the only thing that could still add rows.
                          const loading = searching
                            ? searchLoading && results.length === 0
                            : defaultLoading;
                          return (
                            <div
                              className="cdv-search-dropdown"
                              // Prevent the input from blurring on click so the
                              // row's onClick fires and the dropdown stays open —
                              // lets the reviewer add several tests in a row.
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              {loading ? (
                                <div className="cdv-search-dropdown-item cdv-search-loading">
                                  {searching
                                    ? "Searching..."
                                    : "Loading tests..."}
                                </div>
                              ) : results.length > 0 ? (
                                results.map((result) => (
                                  <div
                                    key={result.id}
                                    className="cdv-search-dropdown-item"
                                    onClick={() =>
                                      handleSelectSearchResult(result)
                                    }
                                  >
                                    <div className="cdv-search-result-info">
                                      <div className="cdv-search-result-name">
                                        <span>
                                          {result.serviceName || result.name}
                                        </span>
                                        {planWalletIncluded &&
                                  !walletBlockedForMember &&
                                          result.isEligibleForWallet !==
                                            true && (
                                            <span className="cdv-cart-wallet-tag">
                                              Wallet not eligible
                                            </span>
                                          )}
                                      </div>
                                      <div className="cdv-search-result-meta">
                                        {`Contains ${result.subTestCount} tests`}
                                        {result.matchedTest &&
                                          ` · Includes ${result.matchedTest.name}${
                                            result.matchedTest.loincCode
                                              ? ` (${result.matchedTest.loincCode})`
                                              : ""
                                          }`}
                                      </div>
                                    </div>
                                    <span className="cdv-search-result-add">
                                      + Add
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <div className="cdv-search-dropdown-item cdv-search-loading">
                                  {searching
                                    ? "No results found"
                                    : defaultResults.length > 0
                                      ? "All available tests are in the cart"
                                      : "No tests available"}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                    </div>
                  )}

                  {/* Test List */}
                  {cartItems.length > 0 && (
                    <div className="cdv-cart-test-list">
                      {/* Bookable tests first, blocked ones (unavailable / not eligible) at bottom */}
                      {cartItems
                        .slice()
                        .sort((a, b) =>
                          isBlockedTest(a) === isBlockedTest(b)
                            ? 0
                            : isBlockedTest(a)
                              ? 1
                              : -1,
                        )
                        .map((test) => (
                          <div
                            key={test.id}
                            className={`cdv-cart-test-item ${
                              cartConfirmed && isBlockedTest(test)
                                ? "cdv-cart-test-unavailable"
                                : ""
                            }`}
                          >
                            {/* Blocker label. "Not available" wins when a test is both, since a
                                provider that does not offer the test at all is the stronger
                                statement — eligibility is moot until that is resolved. */}
                            {cartConfirmed && isBlockedTest(test) && (
                              <div className="cdv-cart-test-not-available-label">
                                {!test.available
                                  ? "Not available"
                                  : "Not eligible"}
                              </div>
                            )}
                            <div className="cdv-cart-test-row">
                              <div className="cdv-cart-test-info">
                                <div className="cdv-cart-test-name">
                                  <span className="cdv-cart-test-name-text">
                                    {test.serviceName || test.name}
                                  </span>
                                  {planWalletIncluded &&
                                  !walletBlockedForMember &&
                                    test.isEligibleForWallet !== true && (
                                      <span className="cdv-cart-wallet-tag">
                                        Wallet not eligible
                                      </span>
                                    )}
                                </div>
                                <div
                                  className="cdv-cart-test-toggle"
                                  role="button"
                                  aria-expanded={expandedTestIds.has(test.id)}
                                  onClick={() => toggleTestIncludes(test.id)}
                                >
                                  {`Contains ${test.subTestCount} tests`}
                                  <span
                                    className={`cdv-cart-test-chevron${
                                      expandedTestIds.has(test.id)
                                        ? " cdv-cart-test-chevron--open"
                                        : ""
                                    }`}
                                  >
                                    <FontIcon
                                      className="notif-preference-icon"
                                      style={{ color: "#f97f0f" }}
                                    >
                                      chevron_right
                                    </FontIcon>
                                  </span>
                                </div>
                                {expandedTestIds.has(test.id) && (
                                  <TestIncludesList includes={test.includes} />
                                )}

                                {cartConfirmed && test.price != null && (
                                  <div className="cdv-cart-test-price">
                                    {formatPrice(test.price)}
                                  </div>
                                )}
                              </div>
                              <div
                                className="cdv-cart-test-delete"
                                // Blocked while a cart operation is in flight (e.g. the
                                // re-fetch after a delete) to avoid overlapping removals.
                                onClick={() => {
                                  if (!cartLoading) onRemoveFromCart(test.id);
                                }}
                                style={
                                  cartLoading
                                    ? { pointerEvents: "none", opacity: 0.4 }
                                    : undefined
                                }
                              >
                                <img
                                  src={deleteIcon}
                                  alt="Remove"
                                  className="cdv-cart-test-delete-icon"
                                />
                              </div>
                            </div>
                            {renderTestTags(test)}
                            {/* Why this patient is not eligible — the backend's own copy, per
                                service, so it names the actual rule ("only for Female", an age
                                band). Unlike the shared unavailable hint below it cannot be
                                written once for the list. */}
                            {cartConfirmed &&
                              test.available &&
                              test.notEligible && (
                                <div className="cdv-cart-test-not-eligible-msg">
                                  {test.notEligibleMsg ||
                                    "*This test is not eligible for the selected patient"}
                                </div>
                              )}
                          </div>
                        ))}

                      {/* Unavailable hint. Not-eligible tests are excluded — each already
                          carries its own reason inside its card. */}
                      {cartConfirmed &&
                        cartItems.some((item) => !item.available) && (
                          <div className="cdv-cart-unavailable-hint">
                            *Either change the service provider or remove the
                            test from cart
                          </div>
                        )}
                    </div>
                  )}
                </div>

                {/* Bottom Action Buttons — pinned to the bottom of the cart
                    panel, outside the scrollable body */}
                {cartItems.length > 0 && (
                  <div className="cdv-cart-actions">
                    {cartConfirmed ? (
                      <div className="cdv-cart-actions-confirmed">
                        <Button
                          raised
                          className={`cdv-btn-book ${
                            !allTestsAvailable ||
                            hasNotEligibleTests ||
                            !selectedProvider
                              ? "cdv-btn-disabled"
                              : ""
                          }`}
                          onClick={onBookAppointment}
                          // Also gated on selectedProvider: after a deletion the previously
                          // selected provider may no longer be covered, leaving the dropdown
                          // unselected — booking must not proceed without a provider.
                          disabled={
                            !allTestsAvailable ||
                            hasNotEligibleTests ||
                            cartLoading ||
                            !selectedProvider
                          }
                        >
                          {cartLoading ? "Loading..." : "Book appointment"}
                        </Button>
                        <Button
                          raised
                          className="cdv-btn-modify"
                          onClick={onModifyCart}
                          disabled={cartLoading}
                        >
                          Modify cart
                        </Button>
                      </div>
                    ) : (
                      <Button
                        raised
                        className="cdv-btn-confirm"
                        onClick={onConfirmCart}
                        disabled={cartLoading}
                      >
                        {cartLoading ? "Confirming..." : "Confirm cart"}
                      </Button>
                    )}
                  </div>
                )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default DocCartView;
